import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "./api";

const AuthCtx = createContext(null);

// Legacy → RBEAM role mapping (must mirror /app/backend/rbac.py)
// "admin" is a first-class canonical role now — not a legacy alias for super_admin.
const LEGACY_TO_CANONICAL = {
  leadership: "founder",
  marketing: "growth_marketing",
};

// Mirrors backend can_assign_partners() in rbac.py.
const MARKETING_ACTION_ROLES = new Set(["super_admin", "admin", "founder", "growth_marketing"]);

export function canonicalRole(role) {
  if (!role) return "partner";
  return LEGACY_TO_CANONICAL[role] || role;
}

// Mirrors backend can_crawl_signals() in rbac.py
const CRAWL_ROLES = new Set(["super_admin", "admin", "founder", "growth_marketing", "ergoniq_sales"]);

export function hasMarketingRole(role) {
  return MARKETING_ACTION_ROLES.has(canonicalRole(role));
}

/** Prefer server flag from /auth/me; fall back to local role map when stale. */
export function canCrawlSignals(user) {
  if (user?.can_crawl_signals === true) return true;
  if (user?.can_crawl_signals === false) return false;
  return CRAWL_ROLES.has(canonicalRole(user?.role));
}

/** Prefer server flag from /auth/me; fall back to local role map when stale. */
export function canAssignPartners(user) {
  if (user?.can_assign_partners === true) return true;
  if (user?.can_assign_partners === false) return false;
  return hasMarketingRole(user?.role);
}

/** Founder, Super Admin, or Admin: split commission pool across partners (R-08). */
export function canSplitCommission(user) {
  if (user?.can_split_commission === true) return true;
  if (user?.can_split_commission === false) return false;
  const role = canonicalRole(user?.role);
  return role === "super_admin" || role === "admin" || role === "founder";
}

/** Internal roles that may see full commission pool / split breakdown (not partners). */
const COMMISSION_DETAIL_ROLES = new Set(["super_admin", "admin", "founder", "growth_marketing", "ergoniq_sales"]);

export function canViewPartnerCommissionDetails(user) {
  return COMMISSION_DETAIL_ROLES.has(canonicalRole(user?.role));
}

/** Universe company delete — mirrors DELETE /companies (blocks partner roles only). */
export function canDeleteUniverseCompany(user) {
  if (!user) return false;
  const role = canonicalRole(user?.role);
  if (role === "partner") return false;
  return Boolean(user?.permissions_write?.universe);
}

/** Trash restore requires write on at least one pipeline module. */
export function canAccessTrash(user) {
  if (!user?.permissions_write) return false;
  const w = user.permissions_write;
  return Boolean(w.universe || w.signals || w.leads || w.opportunities);
}

const TRASH_RESTORE_MODULE = {
  company: "universe",
  signal: "signals",
  lead: "leads",
  opportunity: "opportunities",
};

export function canRestoreTrashType(user, entityType) {
  const module = TRASH_RESTORE_MODULE[entityType];
  if (!module) return false;
  return Boolean(user?.permissions_write?.[module]);
}

/** Row-level opportunity edits — excludes Growth read-only. */
export function canEditOpportunities(user) {
  if (!user) return false;
  const role = canonicalRole(user?.role);
  if (role === "growth_marketing") return false;
  return Boolean(user?.permissions_write?.opportunities);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hydrate permissions from server on load so the flag map is always fresh
  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("ros_user", JSON.stringify(data));
      setUser(data);
      return data;
    } catch (err) {
      // 401 is handled by the api interceptor (redirect to login).
      // 404 means the JWT user no longer exists (DB re-seeded) — force a clean sign-in.
      if (err.response?.status === 401 || err.response?.status === 404) {
        localStorage.removeItem("ros_token");
        localStorage.removeItem("ros_user");
        setUser(null);
        return null;
      }
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : "Could not load your permissions. Check your connection and try signing in again.";
      toast.error(message);
      throw err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      const stored = localStorage.getItem("ros_user");
      const token = localStorage.getItem("ros_token");
      if (!stored || !token) {
        if (!cancelled) setLoading(false);
        return;
      }

      let parsed = null;
      try {
        parsed = JSON.parse(stored);
        if (!cancelled) setUser(parsed);
      } catch {
        localStorage.removeItem("ros_user");
        localStorage.removeItem("ros_token");
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        await refreshMe();
      } catch {
        // refreshMe clears session on 404; other failures drop stale user if no permissions.
        if (!cancelled && !localStorage.getItem("ros_token")) {
          setUser(null);
        } else if (!parsed?.permissions) {
          localStorage.removeItem("ros_user");
          localStorage.removeItem("ros_token");
          if (!cancelled) setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    hydrate();
    return () => { cancelled = true; };
  }, [refreshMe]);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ros_token", data.token);
    try {
      // Nav and module access depend on /auth/me permissions — do not proceed without it.
      const { data: me } = await api.get("/auth/me");
      localStorage.setItem("ros_user", JSON.stringify(me));
      setUser(me);
      return me;
    } catch (err) {
      localStorage.removeItem("ros_token");
      localStorage.removeItem("ros_user");
      setUser(null);
      throw err;
    }
  }

  function logout() {
    // Best-effort audit record — fire before clearing the token, don't block on it.
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ros_token");
    localStorage.removeItem("ros_user");
    setUser(null);
    window.location.href = "/login";
  }

  // Permission helpers — reads server-provided maps from /auth/me
  const can = (module) => Boolean(user?.permissions?.[module]);
  const canWrite = (module) => Boolean(user?.permissions_write?.[module]);
  const role = canonicalRole(user?.role);
  const isSuperAdmin = role === "super_admin";
  const isAdmin = role === "admin";
  const isFounder = role === "founder";
  const isPartner = role === "partner";

  return (
    <AuthCtx.Provider value={{
      user, login, logout, loading, refreshMe,
      can, canWrite, canEditOpportunities: () => canEditOpportunities(user),
      role, isSuperAdmin, isAdmin, isFounder, isPartner,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
